// Influencers & Sources tab.
//
// 2026-05-27 redesign — replaced the dense 6-column table with a
// dashboard layout matching the Demographics tab pattern:
//   • 4-card hero stat row (Total / Reach / Top Voice / Risk-tagged)
//   • Top-3 podium with avatars (gold/silver/bronze)
//   • Platform-mix donut + sort controls
//   • Risk-tagged section pinned at top with red border
//   • Row-cards (avatar identity | metrics cluster | influence + drill-in)
//
// Sources sub-tab kept lighter — just stat row + ranked platform cards.
//
// All visualisations are inline SVG; AccountAvatar / PlatformChip /
// PlatformAvatarDisc come from the existing `_avatarKit`.
import React, { useEffect, useMemo, useState } from 'react';
import { bp_influencers, bp_sources } from '../../../services';
import { formatNumber, unwrap, EmptyState } from './_shared';
import {
    PlatformIcon, PlatformAvatarDisc, AccountAvatar,
    PlatformChip, profileUrlFor,
} from './_avatarKit';
import { Icon } from './_icons';

const SUB_TABS = [
    { key: 'influencers', label: 'Influencers' },
    { key: 'sources',     label: 'Sources' },
];

const SORT_OPTIONS = [
    { key: 'mentions',            label: 'Mentions' },
    { key: 'total_reach',         label: 'Reach' },
    { key: 'followers',           label: 'Followers' },
    { key: 'avg_influence_score', label: 'Influence score' },
    { key: 'share_of_voice',      label: 'Share of voice' },
    { key: 'neg_share',           label: 'Negative share' },
];

const SENT_COLOR = { positive: '#16a34a', neutral: '#94a3b8', negative: '#dc2626' };

// Compact 3-segment sentiment bar (positive / neutral / negative). `compact`
// drops the count labels for tight row contexts.
function SentimentBar({ sentiment, total, width = 120, compact = false }) {
    const s = sentiment || {};
    const pos = s.positive || 0, neg = s.negative || 0;
    const neu = s.neutral != null ? s.neutral : Math.max(0, (total || 0) - pos - neg);
    const sum = pos + neu + neg || 1;
    const seg = (n, c) => n > 0 && (
        <span style={{ width: `${(n / sum) * 100}%`, background: c, height: '100%',
                       display: 'inline-block' }} />
    );
    return (
        <span title={`${pos} positive · ${neu} neutral · ${neg} negative`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <span style={{ width, height: 6, borderRadius: 999, overflow: 'hidden',
                           display: 'inline-flex', background: '#f1f5f9' }}>
                {seg(pos, SENT_COLOR.positive)}
                {seg(neu, SENT_COLOR.neutral)}
                {seg(neg, SENT_COLOR.negative)}
            </span>
            {!compact && (
                <span style={{ fontSize: 10, color: '#64748b', whiteSpace: 'nowrap',
                               fontVariantNumeric: 'tabular-nums' }}>
                    {neg > 0
                        ? <span style={{ color: '#b91c1c', fontWeight: 600 }}>{neg} neg</span>
                        : <span style={{ color: '#16a34a' }}>clean</span>}
                </span>
            )}
        </span>
    );
}

const DONUT_PALETTE = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444',
    '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16',
];

function drillIntoMentionsForAuthor(handle) {
    window.dispatchEvent(new CustomEvent('bm-mentions-set-filter',
        { detail: { author: handle } }));
    window.dispatchEvent(new CustomEvent('bm-tab-switch',
        { detail: { tab: 'mentions' } }));
}

function drillIntoMentionsForPlatform(platform) {
    window.dispatchEvent(new CustomEvent('bm-mentions-set-filter',
        { detail: { platform } }));
    window.dispatchEvent(new CustomEvent('bm-tab-switch',
        { detail: { tab: 'mentions' } }));
}

// ---------------------------------------------------------------------
// Reusable atoms
// ---------------------------------------------------------------------

function StatCard({ label, value, sub, accent = '#3b82f6', icon }) {
    return (
        <div style={{
            flex: 1, minWidth: 180,
            padding: '14px 16px',
            background: '#ffffff',
            border: '1px solid rgba(148,163,184,0.30)',
            borderRadius: 10,
            position: 'relative', overflow: 'hidden',
        }}>
            <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                background: `linear-gradient(90deg, ${accent}, ${accent}88)`,
            }} />
            <div style={{
                fontSize: 10, fontWeight: 700, letterSpacing: 0.6,
                textTransform: 'uppercase', color: '#64748b',
                marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6,
            }}>
                {icon && <Icon name={icon} size={14} color={accent} />}
                {label}
            </div>
            <div style={{
                fontSize: 26, fontWeight: 700, color: '#0f172a',
                lineHeight: 1.1, fontFamily: 'ui-sans-serif, system-ui',
            }}>
                {value}
            </div>
            {sub && (
                <div style={{
                    marginTop: 4, fontSize: 11, color: '#64748b',
                    lineHeight: 1.4,
                }}>
                    {sub}
                </div>
            )}
        </div>
    );
}

function InfluencerPodium({ items, onClick }) {
    if (!items || items.length === 0) return null;
    const top3 = items.slice(0, 3);
    const TINTS = [
        { bg: 'linear-gradient(135deg, #fde68a, #fef3c7)', border: '#f59e0b', rank: '#92400e' },
        { bg: 'linear-gradient(135deg, #e2e8f0, #f1f5f9)', border: '#94a3b8', rank: '#475569' },
        { bg: 'linear-gradient(135deg, #fed7aa, #ffedd5)', border: '#ea580c', rank: '#9a3412' },
    ];
    return (
        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            {top3.map((i, idx) => {
                const profileUrl = profileUrlFor(i.handle, i.platform);
                return (
                    <button key={(i.handle || '') + (i.platform || '')}
                            type="button"
                            onClick={() => onClick && onClick(i.handle)}
                            title={`Click to see what @${i.handle} posted about your brand`}
                            style={{
                                flex: 1, padding: '14px 14px 12px', borderRadius: 10,
                                background: TINTS[idx].bg,
                                border: `1px solid ${TINTS[idx].border}55`,
                                position: 'relative', overflow: 'hidden',
                                cursor: 'pointer', textAlign: 'left',
                                transition: 'transform 120ms, box-shadow 120ms',
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 14px rgba(15,23,42,0.08)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.transform = 'none';
                                e.currentTarget.style.boxShadow = 'none';
                            }}>
                        <div style={{
                            position: 'absolute', top: 8, right: 10,
                            fontSize: 10, fontWeight: 700, letterSpacing: 0.4,
                            color: TINTS[idx].rank,
                            background: 'rgba(255,255,255,0.65)',
                            padding: '2px 6px', borderRadius: 999,
                        }}>
                            #{idx + 1}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <AccountAvatar handle={i.handle} platform={i.platform} size={50} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                    fontSize: 14, fontWeight: 700, color: '#0f172a',
                                    overflow: 'hidden', textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap', maxWidth: 160,
                                }} title={i.handle}>
                                    {i.handle}
                                </div>
                                <div style={{ marginTop: 4 }}>
                                    <PlatformChip platform={i.platform} />
                                </div>
                            </div>
                        </div>
                        <div style={{
                            marginTop: 10, display: 'flex', alignItems: 'baseline',
                            gap: 10, flexWrap: 'wrap',
                        }}>
                            <span style={{ fontSize: 22, fontWeight: 700, color: '#0f172a',
                                           fontVariantNumeric: 'tabular-nums' }}>
                                {i.avg_influence_score || 0}<span style={{ fontSize: 12, color: '#64748b' }}>/10</span>
                            </span>
                            <span style={{ fontSize: 11, color: '#475569' }}>
                                {i.mentions || 0} mention{i.mentions === 1 ? '' : 's'} ·
                                {' '}{formatNumber(i.followers || 0)} followers
                            </span>
                        </div>
                        <div style={{ marginTop: 8 }}>
                            <SentimentBar sentiment={i.sentiment} total={i.mentions}
                                          width={120} compact />
                        </div>
                        {i.risk_tagged && (
                            <span style={{
                                position: 'absolute', bottom: 8, right: 10,
                                fontSize: 9, fontWeight: 700, letterSpacing: 0.5,
                                color: '#b91c1c', background: 'rgba(255,255,255,0.7)',
                                padding: '2px 6px', borderRadius: 999,
                                border: '1px solid rgba(220,38,38,0.45)',
                            }}>
                                RISK SOURCE
                            </span>
                        )}
                        {profileUrl && (
                            <a href={profileUrl} target="_blank" rel="noopener noreferrer"
                               onClick={e => e.stopPropagation()}
                               style={{
                                   position: 'absolute', bottom: 8, left: 10,
                                   fontSize: 10, color: '#475569', textDecoration: 'none',
                                   padding: '1px 5px', borderRadius: 4,
                                   background: 'rgba(255,255,255,0.55)',
                               }}>
                                ↗ profile
                            </a>
                        )}
                    </button>
                );
            })}
            {/* Placeholders if we have <3 */}
            {[...Array(Math.max(0, 3 - top3.length))].map((_, idx) => (
                <div key={`empty-${idx}`}
                     style={{
                        flex: 1, padding: '14px 14px 12px', borderRadius: 10,
                        background: 'rgba(248,250,252,0.7)',
                        border: '1px dashed rgba(148,163,184,0.4)',
                        opacity: 0.6, minHeight: 110,
                     }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 50, height: 50, borderRadius: '50%',
                                      background: 'rgba(148,163,184,0.18)' }} />
                        <div style={{ color: '#94a3b8', fontSize: 12 }}>
                            Awaiting more voices
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function PlatformMixDonut({ items, valueKey, title = 'Platform mix', valueLabel = 'reach' }) {
    // Aggregate the per-influencer rows into per-platform totals.
    const byPlat = {};
    items.forEach(i => {
        const p = (i.platform || 'unknown').toLowerCase();
        byPlat[p] = (byPlat[p] || 0) + (i[valueKey] || 0);
    });
    const slices = Object.entries(byPlat)
        .map(([platform, value]) => ({ platform, value }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 8);
    const sum = slices.reduce((s, x) => s + x.value, 0) || 1;
    const size = 160, stroke = 22, r = (size - stroke) / 2;
    const circ = 2 * Math.PI * r;
    let acc = 0;
    const arcs = slices.map((s, i) => {
        const frac = s.value / sum;
        const dash = `${frac * circ} ${circ}`;
        const offset = -acc * circ;
        acc += frac;
        return { ...s, color: DONUT_PALETTE[i % DONUT_PALETTE.length], dash, offset, frac };
    });
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
                 style={{ flexShrink: 0 }}>
                <circle cx={size / 2} cy={size / 2} r={r}
                        fill="none" stroke="#f1f5f9" strokeWidth={stroke} />
                {arcs.map((a, i) => (
                    <circle key={i}
                            cx={size / 2} cy={size / 2} r={r}
                            fill="none" stroke={a.color} strokeWidth={stroke}
                            strokeDasharray={a.dash}
                            strokeDashoffset={a.offset}
                            transform={`rotate(-90 ${size / 2} ${size / 2})`}>
                        <title>{a.platform}: {a.value.toFixed(1)} ({(a.frac * 100).toFixed(1)}%)</title>
                    </circle>
                ))}
                <text x={size / 2} y={size / 2 - 4}
                      textAnchor="middle" fontSize={22} fontWeight={700}
                      fill="#0f172a" fontFamily="ui-sans-serif, system-ui">
                    {slices.length}
                </text>
                <text x={size / 2} y={size / 2 + 14}
                      textAnchor="middle" fontSize={9} fontWeight={600}
                      letterSpacing={0.8} fill="#64748b">
                    PLATFORMS
                </text>
            </svg>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
                <div style={{
                    fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                    color: '#64748b', textTransform: 'uppercase',
                    marginBottom: 2,
                }}>
                    {title} · by {valueLabel}
                </div>
                {arcs.map((a, i) => (
                    <div key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        fontSize: 11.5,
                    }}>
                        <span style={{
                            width: 10, height: 10, borderRadius: 2,
                            background: a.color, flexShrink: 0,
                        }} />
                        <PlatformIcon platform={a.platform} size={11} />
                        <span style={{ flex: 1, color: '#0f172a', fontWeight: 500,
                                       textTransform: 'capitalize' }}>
                            {a.platform}
                        </span>
                        <span style={{
                            color: '#334155', fontVariantNumeric: 'tabular-nums',
                        }}>
                            {(a.frac * 100).toFixed(1)}%
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

// Compact row-card: avatar + identity | metrics | influence + drill arrow
function InfluencerRow({ i, rank, totalMentions, maxReach, onClick }) {
    const sov = totalMentions ? ((i.mentions || 0) / totalMentions * 100).toFixed(1) : '0.0';
    const score = i.avg_influence_score || 0;
    const profileUrl = profileUrlFor(i.handle, i.platform);
    const reachPct = maxReach ? Math.min(100, ((i.total_reach || 0) / maxReach) * 100) : 0;
    return (
        <div onClick={() => onClick && onClick(i.handle)}
             title={`Click to see what @${i.handle} posted about your brand`}
             style={{
                 display: 'grid',
                 gridTemplateColumns: '28px 56px 1fr 280px 200px 28px',
                 alignItems: 'center', gap: 12,
                 padding: '10px 14px',
                 borderRadius: 8,
                 background: i.risk_tagged ? 'rgba(254,226,226,0.4)' : '#ffffff',
                 border: i.risk_tagged
                     ? '1px solid rgba(220,38,38,0.45)'
                     : '1px solid rgba(148,163,184,0.25)',
                 borderLeft: i.risk_tagged
                     ? '3px solid #dc2626'
                     : '1px solid rgba(148,163,184,0.25)',
                 cursor: 'pointer',
                 transition: 'background 120ms, transform 120ms',
             }}
             onMouseEnter={e => {
                 e.currentTarget.style.background = i.risk_tagged
                     ? 'rgba(254,226,226,0.55)'
                     : 'rgba(241,245,249,0.7)';
             }}
             onMouseLeave={e => {
                 e.currentTarget.style.background = i.risk_tagged
                     ? 'rgba(254,226,226,0.4)'
                     : '#ffffff';
             }}>
            {/* Rank */}
            <span style={{
                fontSize: 11, fontWeight: 700, color: '#94a3b8',
                textAlign: 'right',
            }}>
                #{rank}
            </span>
            {/* Avatar */}
            <AccountAvatar handle={i.handle} platform={i.platform} size={44} />
            {/* Identity */}
            <div style={{ minWidth: 0 }}>
                <div style={{
                    fontSize: 13, fontWeight: 600, color: '#0f172a',
                    display: 'flex', alignItems: 'center', gap: 6,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                    <span title={i.handle}>{i.handle}</span>
                    {i.risk_tagged && (
                        <span style={{
                            fontSize: 9, fontWeight: 700, letterSpacing: 0.5,
                            color: '#b91c1c', background: 'rgba(220,38,38,0.10)',
                            padding: '1px 6px', borderRadius: 999,
                            border: '1px solid rgba(220,38,38,0.45)',
                            whiteSpace: 'nowrap',
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                        }}>
                            <Icon name="alert" size={9} /> RISK
                        </span>
                    )}
                    {profileUrl && (
                        <a href={profileUrl} target="_blank" rel="noopener noreferrer"
                           onClick={e => e.stopPropagation()}
                           title={`Open @${i.handle} on ${i.platform}`}
                           style={{
                               fontSize: 10, color: '#94a3b8', textDecoration: 'none',
                           }}>
                            ↗
                        </a>
                    )}
                </div>
                <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <PlatformChip platform={i.platform} />
                </div>
            </div>
            {/* Metrics cluster */}
            <div style={{
                fontSize: 11, color: '#475569', lineHeight: 1.6,
                fontVariantNumeric: 'tabular-nums',
            }}>
                <div>
                    <b style={{ color: '#0f172a' }}>{i.mentions || 0}</b>
                    <span style={{ color: '#94a3b8' }}> mentions ·</span>
                    {' '}<b style={{ color: '#0f172a' }}>{formatNumber(i.followers || 0)}</b>
                    <span style={{ color: '#94a3b8' }}> followers</span>
                </div>
                <div>
                    <span style={{ color: '#94a3b8' }}>Share of voice:</span>
                    {' '}<b style={{ color: '#0f172a' }}>{sov}%</b>
                    <span style={{ color: '#94a3b8' }}> · Reach score:</span>
                    {' '}<b style={{ color: '#0f172a' }}>{(i.total_reach || 0).toFixed(2)}</b>
                </div>
                <div style={{ marginTop: 3 }}>
                    <span style={{ color: '#94a3b8', marginRight: 6 }}>Sentiment:</span>
                    <SentimentBar sentiment={i.sentiment} total={i.mentions} width={96} />
                </div>
            </div>
            {/* Influence — score + bar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ position: 'relative', flex: 1, height: 6 }}>
                    <span style={{
                        display: 'block', position: 'absolute', inset: 0,
                        background: '#e2e8f0', borderRadius: 999,
                    }} />
                    <span style={{
                        display: 'block', position: 'absolute', left: 0, top: 0, bottom: 0,
                        width: (score * 10) + '%',
                        background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
                        borderRadius: 999,
                    }} />
                </span>
                <span style={{
                    fontSize: 13, fontWeight: 700, color: '#0f172a',
                    fontVariantNumeric: 'tabular-nums', minWidth: 38, textAlign: 'right',
                }}>
                    {score}<span style={{ fontSize: 10, color: '#64748b' }}>/10</span>
                </span>
                {/* Tiny reach indicator */}
                <span style={{ position: 'relative', width: 36, height: 4 }}
                      title={`Reach within visible set: ${reachPct.toFixed(0)}%`}>
                    <span style={{
                        display: 'block', position: 'absolute', inset: 0,
                        background: '#e2e8f0', borderRadius: 999,
                    }} />
                    <span style={{
                        display: 'block', position: 'absolute', left: 0, top: 0, bottom: 0,
                        width: reachPct + '%',
                        background: '#10b981', borderRadius: 999,
                    }} />
                </span>
            </div>
            {/* Drill-in arrow */}
            <span style={{
                fontSize: 16, color: '#cbd5e1',
                transition: 'color 120ms, transform 120ms',
            }}>
                →
            </span>
        </div>
    );
}

function SourceRow({ s, rank, maxMentions, maxReach, onClick }) {
    const mPct = maxMentions ? Math.min(100, ((s.mentions || 0) / maxMentions) * 100) : 0;
    return (
        <div onClick={() => onClick && onClick(s.platform)}
             title={`Click to see all mentions captured from ${s.platform}`}
             style={{
                 display: 'grid',
                 gridTemplateColumns: '28px 56px 1fr 240px 100px 28px',
                 alignItems: 'center', gap: 12,
                 padding: '10px 14px',
                 borderRadius: 8,
                 background: '#ffffff',
                 border: '1px solid rgba(148,163,184,0.25)',
                 cursor: 'pointer',
                 transition: 'background 120ms',
             }}
             onMouseEnter={e => e.currentTarget.style.background = 'rgba(241,245,249,0.7)'}
             onMouseLeave={e => e.currentTarget.style.background = '#ffffff'}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textAlign: 'right' }}>
                #{rank}
            </span>
            <PlatformAvatarDisc platform={s.platform} size={44} />
            <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a',
                              textTransform: 'capitalize' }}>
                    {s.platform}
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                    {s.mentions} mention{s.mentions === 1 ? '' : 's'}
                </div>
            </div>
            <span style={{ position: 'relative', flex: 1, height: 6 }}
                  title={`${s.mentions} of ${maxMentions} max in window`}>
                <span style={{
                    display: 'block', position: 'absolute', inset: 0,
                    background: '#e2e8f0', borderRadius: 999,
                }} />
                <span style={{
                    display: 'block', position: 'absolute', left: 0, top: 0, bottom: 0,
                    width: mPct + '%',
                    background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
                    borderRadius: 999,
                }} />
            </span>
            <span style={{
                fontSize: 13, fontWeight: 700, color: '#0f172a',
                fontVariantNumeric: 'tabular-nums', textAlign: 'right',
            }}>
                {s.share_pct.toFixed(1)}%
            </span>
            <span style={{ fontSize: 16, color: '#cbd5e1' }}>→</span>
        </div>
    );
}

// ---------------------------------------------------------------------
// Main tab
// ---------------------------------------------------------------------

export default function InfluencersTab() {
    const [days, setDays]               = useState(30);
    const [subTab, setSubTab]           = useState('influencers');
    const [influencers, setInfluencers] = useState([]);
    const [sources, setSources]         = useState([]);
    const [sortKey, setSortKey]         = useState('mentions');
    const [sortDir, setSortDir]         = useState('desc');

    useEffect(() => {
        Promise.allSettled([
            bp_influencers({ days, limit: 50 }),
            bp_sources({ days }),
        ]).then(([i, s]) => {
            const ok = r => r?.status === 'fulfilled' ? r.value : null;
            setInfluencers((unwrap(ok(i)) || {}).influencers || []);
            setSources((unwrap(ok(s)) || {}).sources || []);
        });
    }, [days]);

    const totalMentions = useMemo(() =>
        influencers.reduce((s, i) => s + (i.mentions || 0), 0) || 1,
    [influencers]);

    const totalSourceMentions = useMemo(() =>
        sources.reduce((s, x) => s + (x.mentions || 0), 0) || 1,
    [sources]);

    // True only while the user is on the default Mentions/desc sort. The
    // moment they pick a different sort key or flip the direction, we
    // honor their explicit choice instead of pinning risk-tagged rows to
    // the top — otherwise the dropdown looks broken because the visible
    // order can't change.
    const isDefaultSort = sortKey === 'mentions' && sortDir === 'desc';

    const sortedInfluencers = useMemo(() => {
        const withSov = influencers.map(i => ({
            ...i,
            share_of_voice: ((i.mentions || 0) / totalMentions) * 100,
            neg_share: ((i.sentiment?.negative || 0) / Math.max(1, i.mentions || 0)) * 100,
        }));
        const cmp = (a, b) => {
            const av = a[sortKey] ?? 0, bv = b[sortKey] ?? 0;
            return sortDir === 'asc' ? av - bv : bv - av;
        };
        if (isDefaultSort) {
            const risk = withSov.filter(i => i.risk_tagged);
            const rest = withSov.filter(i => !i.risk_tagged);
            risk.sort(cmp); rest.sort(cmp);
            return [...risk, ...rest];
        }
        return withSov.sort(cmp);
    }, [influencers, sortKey, sortDir, totalMentions, isDefaultSort]);

    const sortedSources = useMemo(() => {
        const enriched = sources.map(s => ({
            ...s,
            share_pct: ((s.mentions || 0) / totalSourceMentions) * 100,
        }));
        enriched.sort((a, b) => {
            const av = a[sortKey] ?? 0, bv = b[sortKey] ?? 0;
            return sortDir === 'asc' ? av - bv : bv - av;
        });
        return enriched;
    }, [sources, sortKey, sortDir, totalSourceMentions]);

    const maxReach     = Math.max(1, ...sortedInfluencers.map(i => i.total_reach || 0));
    const maxSrcMents  = Math.max(1, ...sortedSources.map(s => s.mentions || 0));
    const maxSrcReach  = Math.max(1, ...sortedSources.map(s => s.total_reach || 0));

    // Derived stats for the hero row.
    const totalReach   = useMemo(() =>
        influencers.reduce((s, i) => s + (i.total_reach || 0), 0),
    [influencers]);
    const totalFollowers = useMemo(() =>
        influencers.reduce((s, i) => s + (i.followers || 0), 0),
    [influencers]);
    const topVoice = sortedInfluencers[0];
    const riskCount = useMemo(
        () => influencers.filter(i => i.risk_tagged).length,
    [influencers]);

    return (
        <>
            <div className="bm_header">
                <div>
                    <div className="bm_subtitle" style={{ fontSize: 18, fontWeight: 600, color: '#0f172a' }}>
                        Influencers & Sources
                    </div>
                    <div className="bm_subtitle" style={{ marginTop: 4, fontSize: 12, color: '#64748b' }}>
                        Who's driving the conversation, and where. Risk-tagged sources
                        pin to the top — your watchlist setup pays off here.
                    </div>
                </div>
            </div>

            {/* Sub-tab toggle + days + sort */}
            <div className="bm_filters" style={{ alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ display: 'inline-flex', borderRadius: 6,
                              border: '1px solid #cbd5e1', overflow: 'hidden' }}>
                    {SUB_TABS.map(t => (
                        <button key={t.key}
                                onClick={() => setSubTab(t.key)}
                                disabled={subTab === t.key}
                                className={`btn btn-sm ${subTab === t.key ? 'btn-primary' : 'btn-secondary'}`}
                                style={{ borderRadius: 0, opacity: subTab === t.key ? 1 : 0.85 }}>
                            {t.label}
                        </button>
                    ))}
                </div>
                <select value={days} onChange={e => setDays(Number(e.target.value))}>
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                    <option value={365}>Last 12 months</option>
                </select>
                {subTab === 'influencers' && (
                    <>
                        <span style={{ fontSize: 11, color: '#64748b', marginLeft: 6 }}>Sort by</span>
                        <select value={sortKey} onChange={e => setSortKey(e.target.value)}>
                            {SORT_OPTIONS.map(o => (
                                <option key={o.key} value={o.key}>{o.label}</option>
                            ))}
                        </select>
                        <button type="button"
                                onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}
                                title={`Currently ${sortDir === 'desc' ? 'descending' : 'ascending'}`}
                                style={{
                                    padding: '4px 10px', borderRadius: 4,
                                    border: '1px solid #cbd5e1',
                                    background: '#ffffff', color: '#0f172a',
                                    fontSize: 12, fontWeight: 600, cursor: 'pointer',
                                }}>
                            {sortDir === 'desc' ? '↓' : '↑'}
                        </button>
                    </>
                )}
            </div>

            {/* ============ INFLUENCERS ============ */}
            {subTab === 'influencers' && (
                <>
                    {sortedInfluencers.length === 0 && (
                        <EmptyState
                            mark="bars"
                            title="No influencers in this window"
                            body="Influencer rankings populate as the scanner captures mentions from authors meeting the reach floor (default 10k followers). Widen the date range or wait for the next scan cycle."
                        />
                    )}

                    {sortedInfluencers.length > 0 && (
                        <>
                            {/* Hero stats */}
                            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                                <StatCard
                                    label="Total voices"
                                    value={sortedInfluencers.length}
                                    sub={`${totalMentions} mentions across this set`}
                                    accent="#3b82f6" icon="users" />
                                <StatCard
                                    label="Combined reach"
                                    value={formatNumber(Math.round(totalReach))}
                                    sub={`reach score (log-scaled engagement weight)`}
                                    accent="#10b981" icon="broadcast" />
                                <StatCard
                                    label="Top voice"
                                    value={topVoice
                                        ? <span style={{ fontSize: 18 }}>@{(topVoice.handle || '').slice(0, 16)}</span>
                                        : '—'}
                                    sub={topVoice
                                        ? `${topVoice.platform} · influence ${topVoice.avg_influence_score || 0}/10`
                                        : 'no data'}
                                    accent="#8b5cf6" icon="award" />
                                <StatCard
                                    label="Risk-tagged"
                                    value={riskCount}
                                    sub={riskCount > 0
                                        ? (isDefaultSort
                                            ? 'pinned at top of list ↓'
                                            : 'marked by RISK SOURCE chip')
                                        : 'none in this window'}
                                    accent={riskCount > 0 ? '#dc2626' : '#94a3b8'}
                                    icon="alert" />
                            </div>

                            {/* Top-3 podium */}
                            <div style={{
                                background: '#ffffff', borderRadius: 10,
                                border: '1px solid rgba(148,163,184,0.25)',
                                padding: '14px 16px', marginBottom: 14,
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between',
                                              alignItems: 'baseline', marginBottom: 10 }}>
                                    <h4 style={{
                                        margin: 0, color: '#0f172a', fontSize: 13,
                                        textTransform: 'uppercase', letterSpacing: 0.6,
                                    }}>
                                        Top voices
                                    </h4>
                                    <span style={{ fontSize: 11, color: '#94a3b8' }}>
                                        ranked by {SORT_OPTIONS.find(o => o.key === sortKey)?.label.toLowerCase()}
                                    </span>
                                </div>
                                <InfluencerPodium
                                    items={sortedInfluencers}
                                    onClick={drillIntoMentionsForAuthor} />
                            </div>

                            {/* Platform mix donut */}
                            <div style={{
                                background: '#ffffff', borderRadius: 10,
                                border: '1px solid rgba(148,163,184,0.25)',
                                padding: '14px 16px', marginBottom: 14,
                            }}>
                                <PlatformMixDonut items={sortedInfluencers} valueKey="total_reach"
                                                  title="Conversation drivers" valueLabel="combined reach" />
                            </div>

                            {/* Ranked row-cards */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {sortedInfluencers.slice(3).map((i, idx) => (
                                    <InfluencerRow key={(i.handle || '') + (i.platform || '')}
                                                   i={i}
                                                   rank={idx + 4}
                                                   totalMentions={totalMentions}
                                                   maxReach={maxReach}
                                                   onClick={drillIntoMentionsForAuthor} />
                                ))}
                            </div>
                        </>
                    )}
                </>
            )}

            {/* ============ SOURCES ============ */}
            {subTab === 'sources' && (
                <>
                    {sortedSources.length === 0 && (
                        <EmptyState
                            mark="dots"
                            title="No source platforms in this window"
                            body="Sources are derived from where mentions originate. Once mentions are captured across X, Reddit, Bluesky, etc., they'll group by platform here."
                        />
                    )}

                    {sortedSources.length > 0 && (
                        <>
                            {/* Hero stats for Sources */}
                            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                                <StatCard
                                    label="Platforms hosting"
                                    value={sortedSources.length}
                                    sub={`${totalSourceMentions} mentions across all platforms`}
                                    accent="#3b82f6" icon="globe" />
                                <StatCard
                                    label="Top platform"
                                    value={sortedSources[0]
                                        ? <span style={{ textTransform: 'capitalize' }}>{sortedSources[0].platform}</span>
                                        : '—'}
                                    sub={sortedSources[0]
                                        ? `${sortedSources[0].mentions} mentions · ${sortedSources[0].share_pct.toFixed(1)}% share`
                                        : 'no data'}
                                    accent="#8b5cf6" icon="award" />
                                <StatCard
                                    label="Concentration"
                                    value={`${sortedSources[0]?.share_pct.toFixed(0) || 0}%`}
                                    sub={`share held by the top platform`}
                                    accent="#f59e0b" icon="trending-up" />
                                <StatCard
                                    label="Diversity"
                                    value={sortedSources.length >= 5 ? 'Broad' : sortedSources.length >= 3 ? 'Moderate' : 'Narrow'}
                                    sub={`${sortedSources.length} active source${sortedSources.length === 1 ? '' : 's'}`}
                                    accent="#10b981" icon="target" />
                            </div>

                            {/* Source rows */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {sortedSources.map((s, idx) => (
                                    <SourceRow key={s.platform}
                                               s={s}
                                               rank={idx + 1}
                                               maxMentions={maxSrcMents}
                                               maxReach={maxSrcReach}
                                               onClick={drillIntoMentionsForPlatform} />
                                ))}
                            </div>
                        </>
                    )}
                </>
            )}
        </>
    );
}
